import { useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr'
import type { DatesSetArg, EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core'
import type { EventDropArg } from '@fullcalendar/core'
import { Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TASK_TYPE_LABELS } from '@/types/work'
import type {
  CalendarFilters,
  CalendarMemberOption,
  CalendarTask,
  CalendarTeamOption,
  CalendarView,
} from '@/types/work'
import { CalendarFiltersBar } from './_components/CalendarFilters'
import '@/components/work/fullcalendar-theme.css'

interface Props {
  tasks: CalendarTask[]
  teams: CalendarTeamOption[]
  members: CalendarMemberOption[]
  filters: CalendarFilters
  empty_perimeter: boolean
}

// Our URL `view` param ↔ FullCalendar view names.
const VIEW_TO_FC: Record<CalendarView, string> = {
  month: 'dayGridMonth',
  week: 'timeGridWeek',
  day: 'timeGridDay',
}
const FC_TO_VIEW: Record<string, CalendarView> = {
  dayGridMonth: 'month',
  timeGridWeek: 'week',
  timeGridDay: 'day',
}

// Date-only param from LOCAL components — view.currentStart is local
// midnight, so toISOString() in a UTC+1 browser (Tunisia) would roll the
// URL's date back to the previous day/month (Codex P2, PR #158). The
// range bounds (start/end) stay toISOString: they are instants, not days.
function toLocalDateParam(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

function toEvents(tasks: CalendarTask[]): EventInput[] {
  return tasks.map((task) => ({
    id: String(task.id),
    title: task.title,
    start: task.due_at,
    // Server-computed drag gate: completed tasks and tasks the user can't
    // reschedule render but never become draggable. The PATCH endpoint
    // re-checks the policy regardless.
    startEditable: task.can_reschedule,
    extendedProps: { task },
  }))
}

export default function CalendarIndex({ tasks, teams, members, filters, empty_perimeter }: Props) {
  const { props: pageProps } = usePage()
  const currentUserId = pageProps.auth.user?.id ?? null

  // Suppress chip interactions and dim the grid while a window refetch is
  // in flight — chips stay visible, just non-interactive.
  const [isPending, setIsPending] = useState(false)

  // datesSet fires on mount and again on every navigation/view switch.
  // The server already echoed the window it served (filters.start/end), so
  // only a real mismatch triggers a refetch; lastRequested breaks any
  // echo loop should the server normalise the requested window.
  const lastRequested = useRef<string | null>(null)

  // Shared navigation: every calendar request carries the full param set
  // (window + team + members) so URL state survives any single change.
  // `members` rides in the partial reload because a team switch swaps the
  // member option list.
  function navigateCalendar(overrides: Record<string, string | number | string[] | undefined>) {
    const params: Record<string, string | number | string[] | undefined> = {
      view: filters.view,
      date: filters.date,
      start: filters.start ?? undefined,
      end: filters.end ?? undefined,
      team_id: filters.team_id ?? undefined,
      assignee_id: filters.assignee_id,
      ...overrides,
    }
    const clean: Record<string, string | number | string[]> = {}
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || (Array.isArray(v) && v.length === 0)) continue
      clean[k] = v
    }
    router.get('/work/calendar', clean, {
      preserveState: true,
      preserveScroll: true,
      only: ['tasks', 'filters', 'members'],
      onStart: () => setIsPending(true),
      onFinish: () => setIsPending(false),
    })
  }

  function handleDatesSet(arg: DatesSetArg) {
    const view = FC_TO_VIEW[arg.view.type] ?? 'week'

    const inSync =
      filters.start !== null &&
      new Date(filters.start).getTime() === arg.start.getTime() &&
      view === filters.view
    if (inSync) return

    const requestKey = `${arg.view.type}:${arg.startStr}`
    if (lastRequested.current === requestKey) return
    lastRequested.current = requestKey

    navigateCalendar({
      view,
      date: toLocalDateParam(arg.view.currentStart),
      start: arg.start.toISOString(),
      end: arg.end.toISOString(),
    })
  }

  // Switching team resets the member selection (URL included) — the old
  // team's member ids are meaningless against the new team's options.
  function handleTeamChange(teamId: number) {
    navigateCalendar({ team_id: teamId, assignee_id: [] })
  }

  function handleAssigneeChange(assigneeIds: string[]) {
    navigateCalendar({ assignee_id: assigneeIds })
  }

  function handleEventClick(arg: EventClickArg) {
    const task = arg.event.extendedProps.task as CalendarTask
    router.visit(`/work/tasks/${task.id}`)
  }

  // Same contract as the kanban drag (TasksKanbanBoard): a flash alert on
  // the redirect-back means denial → revert the chip; the global useFlash
  // hook already toasts the alert, so no second toast here. A month-view
  // drop keeps the original time of day (FullCalendar preserves it for
  // timed events); event.start is offset-bearing via toISOString, never a
  // hand-formatted local string.
  function handleEventDrop(arg: EventDropArg) {
    const task = arg.event.extendedProps.task as CalendarTask
    const start = arg.event.start
    if (!start) {
      arg.revert()
      return
    }

    router.patch(
      `/work/tasks/${task.id}/update_due_at?source=calendar`,
      { due_at: start.toISOString() },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
          const flash = (page.props as { flash?: { alert?: string } }).flash
          if (flash?.alert) arg.revert()
        },
        onError: () => arg.revert(),
      },
    )
  }

  function renderChip(arg: EventContentArg) {
    const task = arg.event.extendedProps.task as CalendarTask
    const own = task.assignee_user_id !== null && task.assignee_user_id === currentUserId
    const completed = task.status === 'completed'

    return (
      <div
        className={cn(
          'flex h-full w-full items-center gap-1 overflow-hidden rounded-sm px-1.5 py-0.5 text-xs leading-tight',
          // Own tasks: primary background + a left accent so ownership
          // doesn't rely on hue alone; others: neutral muted style.
          own && !completed && 'border-l-4 border-l-primary-foreground/50 bg-primary text-primary-foreground',
          !own && !completed && 'border-l-4 border-l-transparent bg-muted text-foreground',
          // Completed: muted + struck-through regardless of ownership.
          completed && 'border-l-4 border-l-transparent bg-muted text-muted-foreground line-through opacity-70',
          task.can_reschedule ? 'cursor-grab' : 'cursor-pointer',
        )}
        title={`${TASK_TYPE_LABELS[task.task_type]} — ${task.title}`}
      >
        {arg.timeText ? <span className="shrink-0 font-medium tabular-nums">{arg.timeText}</span> : null}
        <span className="truncate">{task.title}</span>
        {!task.can_reschedule && !completed ? (
          <Lock aria-label="Non déplaçable" className="ml-auto size-3 shrink-0 opacity-60" />
        ) : null}
      </div>
    )
  }

  if (empty_perimeter) {
    return (
      <div className="space-y-6">
        <CalendarHeader />
        <div className="rounded-lg border border-dashed p-12 text-center text-muted-foreground">
          Aucune équipe visible — le calendrier s’active dès que vous appartenez à une équipe.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <CalendarHeader />

      <CalendarFiltersBar
        teams={teams}
        members={members}
        filters={filters}
        onTeamChange={handleTeamChange}
        onAssigneeChange={handleAssigneeChange}
      />

      <div
        aria-busy={isPending}
        className={cn(
          'min-h-[560px] rounded-lg border bg-card p-3',
          isPending && 'pointer-events-none opacity-60',
        )}
      >
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          locale={frLocale}
          initialView={VIEW_TO_FC[filters.view]}
          initialDate={filters.date}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={toEvents(tasks)}
          editable
          eventDurationEditable={false}
          defaultTimedEventDuration="00:30:00"
          // Month cells overflow into FullCalendar's "+n more" popover;
          // entries inside it keep the same chip renderer and click-through.
          // Drag from inside the popover stays disabled (calendar-wide
          // `editable` gate in U3; per-event in U5 the popover inherits it).
          dayMaxEvents={3}
          nowIndicator
          scrollTime="08:00:00"
          height="auto"
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventContent={renderChip}
        />
      </div>
    </div>
  )
}

function CalendarHeader() {
  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight">Calendrier</h1>
      <p className="text-muted-foreground">
        Tâches planifiées de votre équipe — appels et rendez-vous inclus.
      </p>
    </div>
  )
}
